import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';

import type { Membership, OrganisationLocation, OrganisationTeam } from '../domain-types';

type PaginatedResponse<T> = {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

type LocationListItem = OrganisationLocation & { personnelCount: number };
type TeamListItem = OrganisationTeam & { memberCount: number };
type ResourceImpact = {
  affected: Array<{ resourceNumber: string; name: string; category: string }>;
};

function selectedIds(event: ChangeEvent<HTMLSelectElement>): string[] {
  return Array.from(event.currentTarget.selectedOptions, (option) => option.value);
}

function useDebouncedValue(value: string, delay = 300): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [delay, value]);
  return debounced;
}

function ListPagination({
  label,
  page,
  total,
  totalPages,
  onPageChange,
}: {
  label: string;
  page: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <nav aria-label={`${label} pages`} className="personnel-pagination">
      <button disabled={page <= 1} type="button" onClick={() => onPageChange(page - 1)}>
        Previous
      </button>
      <span>
        Page {page} of {totalPages} · {total} {label.toLowerCase()}
      </span>
      <button disabled={page >= totalPages} type="button" onClick={() => onPageChange(page + 1)}>
        Next
      </button>
    </nav>
  );
}

function addressLabel(location: OrganisationLocation): string {
  return [
    ...location.addressLines,
    location.suburb,
    location.state,
    location.postcode,
    location.country,
  ]
    .filter(Boolean)
    .join(', ');
}

async function apiRequest<T>(input: {
  apiUrl: string;
  accessToken: string;
  path: string;
  method?: 'GET' | 'POST' | 'PATCH';
  body?: unknown;
  platformSupportReason?: string | undefined;
}): Promise<T> {
  const response = await fetch(`${input.apiUrl}${input.path}`, {
    ...(input.method ? { method: input.method } : {}),
    headers: {
      authorization: `Bearer ${input.accessToken}`,
      ...(input.platformSupportReason
        ? { 'x-quality-vet-support-reason': input.platformSupportReason }
        : {}),
      ...(input.body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    ...(input.body === undefined ? {} : { body: JSON.stringify(input.body) }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return (await response.json()) as T;
}

export function LocationDirectoryPanel({
  apiUrl,
  accessToken,
  tenantId,
  editable,
  platformSupportReason,
  onChanged,
}: {
  apiUrl: string;
  accessToken: string;
  tenantId: string;
  editable: boolean;
  platformSupportReason?: string | undefined;
  onChanged: () => Promise<void>;
}) {
  const [items, setItems] = useState<LocationListItem[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput);
  const [status, setStatus] = useState('active');
  const [stateFilter, setStateFilter] = useState('');
  const [primary, setPrimary] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState<LocationListItem>();
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [addressLines, setAddressLines] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [country, setCountry] = useState('Australia');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  useEffect(() => setPage(1), [search, stateFilter, status, primary]);

  useEffect(() => {
    let active = true;
    const parameters = new URLSearchParams({ page: String(page), status });
    if (search) parameters.set('search', search);
    if (stateFilter) parameters.set('state', stateFilter);
    if (primary) parameters.set('primary', primary);
    void apiRequest<PaginatedResponse<LocationListItem>>({
      apiUrl,
      accessToken,
      platformSupportReason,
      path: `/v1/tenants/${tenantId}/locations?${parameters}`,
    })
      .then((body) => {
        if (!active) return;
        setItems(body.items);
        setTotal(body.total);
        setTotalPages(body.totalPages);
      })
      .catch(() => active && setError('Locations could not be loaded.'));
    return () => {
      active = false;
    };
  }, [
    accessToken,
    apiUrl,
    page,
    platformSupportReason,
    primary,
    refresh,
    search,
    stateFilter,
    status,
    tenantId,
  ]);

  const clearFilters = () => {
    setSearchInput('');
    setStatus('active');
    setStateFilter('');
    setPrimary('');
  };

  const resetForm = () => {
    setName('');
    setCode('');
    setDescription('');
    setAddressLines('');
    setSuburb('');
    setState('');
    setPostcode('');
    setCountry('Australia');
    setPhone('');
    setEmail('');
    setIsPrimary(false);
  };

  const locationPayload = () => {
    const lines = addressLines
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    return {
      name: name.trim(),
      ...(code.trim() ? { code: code.trim() } : {}),
      ...(description.trim() ? { description: description.trim() } : {}),
      addressLines: lines,
      suburb: suburb.trim(),
      state: state.trim(),
      postcode: postcode.trim(),
      country: country.trim() || 'Australia',
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(email.trim() ? { contactEmail: email.trim() } : {}),
      isPrimary,
    };
  };

  const createLocation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    const payload = locationPayload();
    if (
      !payload.name ||
      payload.addressLines.length === 0 ||
      !payload.suburb ||
      !payload.state ||
      !payload.postcode
    ) {
      setError('Enter a name, physical address, suburb, state or territory and postcode.');
      return;
    }
    try {
      await apiRequest({
        apiUrl,
        accessToken,
        platformSupportReason,
        path: `/v1/tenants/${tenantId}/locations`,
        method: 'POST',
        body: payload,
      });
      resetForm();
      setRefresh((value) => value + 1);
      await onChanged();
    } catch {
      setError('The location could not be created. Check the code and contact details.');
    }
  };

  const openLocation = (location: LocationListItem) => {
    setSelected(location);
    setName(location.name);
    setCode(location.code ?? '');
    setDescription(location.description ?? '');
    setAddressLines(location.addressLines.join('\n'));
    setSuburb(location.suburb);
    setState(location.state);
    setPostcode(location.postcode);
    setCountry(location.country);
    setPhone(location.phone ?? '');
    setEmail(location.contactEmail ?? '');
    setIsPrimary(location.isPrimary === true);
  };

  const saveLocation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    setError('');
    try {
      const updated = await apiRequest<LocationListItem>({
        apiUrl,
        accessToken,
        platformSupportReason,
        path: `/v1/tenants/${tenantId}/locations/${selected.id}`,
        method: 'PATCH',
        body: locationPayload(),
      });
      setSelected(updated);
      setRefresh((value) => value + 1);
      await onChanged();
    } catch {
      setError('The location could not be saved.');
    }
  };

  const archiveLocation = async () => {
    if (!selected) return;
    try {
      const impact = await apiRequest<ResourceImpact>({
        apiUrl,
        accessToken,
        platformSupportReason,
        path: `/v1/tenants/${tenantId}/resources/impacts?locationId=${encodeURIComponent(selected.id)}`,
      });
      if (impact.affected.length) {
        const examples = impact.affected
          .slice(0, 3)
          .map((resource) => resource.name)
          .join(', ');
        const confirmation = window.prompt(
          `${impact.affected.length} Resource record(s) reference this Location (${examples}${impact.affected.length > 3 ? ', …' : ''}). Reassign them in Resources first, or type REVIEW to archive and create review-required alerts.`,
        );
        if (confirmation?.trim().toUpperCase() !== 'REVIEW') return;
      } else if (!window.confirm(`Archive ${selected.name}? Historical assignments are retained.`))
        return;
      await apiRequest({
        apiUrl,
        accessToken,
        platformSupportReason,
        path: `/v1/tenants/${tenantId}/locations/${selected.id}`,
        method: 'PATCH',
        body: {
          status: 'archived',
          ...(impact.affected.length ? { resourceImpactDecision: 'review_required' } : {}),
        },
      });
      setSelected(undefined);
      setRefresh((value) => value + 1);
      await onChanged();
    } catch {
      setError('The location could not be archived.');
    }
  };

  if (selected) {
    return (
      <section className="organisation-structure-panel" aria-labelledby="location-profile-title">
        <button type="button" onClick={() => setSelected(undefined)}>
          ← Back to locations
        </button>
        <div className="workspace-heading">
          <div>
            <p className="eyebrow">Location profile</p>
            <h2 id="location-profile-title">{selected.name}</h2>
          </div>
          <span>{selected.status ?? 'active'}</span>
        </div>
        {editable ? (
          <form className="location-form" onSubmit={(event) => void saveLocation(event)}>
            <LocationFields
              name={name}
              code={code}
              description={description}
              addressLines={addressLines}
              suburb={suburb}
              state={state}
              postcode={postcode}
              country={country}
              phone={phone}
              email={email}
              isPrimary={isPrimary}
              onNameChange={setName}
              onCodeChange={setCode}
              onDescriptionChange={setDescription}
              onAddressLinesChange={setAddressLines}
              onSuburbChange={setSuburb}
              onStateChange={setState}
              onPostcodeChange={setPostcode}
              onCountryChange={setCountry}
              onPhoneChange={setPhone}
              onEmailChange={setEmail}
              onPrimaryChange={setIsPrimary}
            />
            <button type="submit">Save location</button>
            <button type="button" onClick={() => void archiveLocation()}>
              Archive location
            </button>
          </form>
        ) : (
          <LocationSummary location={selected} />
        )}
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
        <ProfilePersonnelList
          accessToken={accessToken}
          apiUrl={apiUrl}
          filterName="locationId"
          filterValue={selected.id}
          platformSupportReason={platformSupportReason}
          tenantId={tenantId}
        />
      </section>
    );
  }

  return (
    <section className="organisation-structure-panel" aria-labelledby="locations-title">
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Organisation locations</p>
          <h2 id="locations-title">Locations</h2>
        </div>
        <div className="personnel-heading-actions">
          <label>
            <span className="visually-hidden">Search locations</span>
            <input
              placeholder="Search locations"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>
          <span>{total} locations</span>
        </div>
      </div>
      <div className="personnel-directory-controls" role="search">
        <label>
          Status
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="all">All statuses</option>
          </select>
        </label>
        <label>
          State or territory
          <input value={stateFilter} onChange={(event) => setStateFilter(event.target.value)} />
        </label>
        <label>
          Primary
          <select value={primary} onChange={(event) => setPrimary(event.target.value)}>
            <option value="">All locations</option>
            <option value="true">Primary only</option>
            <option value="false">Non-primary only</option>
          </select>
        </label>
        <button type="button" onClick={clearFilters}>
          Reset filters
        </button>
      </div>
      {editable ? (
        <form className="location-form" onSubmit={(event) => void createLocation(event)}>
          <LocationFields
            name={name}
            code={code}
            description={description}
            addressLines={addressLines}
            suburb={suburb}
            state={state}
            postcode={postcode}
            country={country}
            phone={phone}
            email={email}
            isPrimary={isPrimary}
            onNameChange={setName}
            onCodeChange={setCode}
            onDescriptionChange={setDescription}
            onAddressLinesChange={setAddressLines}
            onSuburbChange={setSuburb}
            onStateChange={setState}
            onPostcodeChange={setPostcode}
            onCountryChange={setCountry}
            onPhoneChange={setPhone}
            onEmailChange={setEmail}
            onPrimaryChange={setIsPrimary}
          />
          <button type="submit">Add location</button>
        </form>
      ) : null}
      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="structure-list">
        {items.length === 0 ? (
          <p className="empty-state">No locations match these filters.</p>
        ) : null}
        {items.map((location) => (
          <article key={location.id}>
            <div>
              <strong>{location.name}</strong>
              <small>{addressLabel(location)}</small>
              <small>
                {location.isPrimary ? 'Primary location · ' : ''}
                {location.personnelCount} active personnel · {location.status ?? 'active'}
              </small>
            </div>
            <button type="button" onClick={() => openLocation(location)}>
              Open
            </button>
          </article>
        ))}
      </div>
      <ListPagination
        label="Locations"
        page={page}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </section>
  );
}

function LocationFields({
  name,
  code,
  description,
  addressLines,
  suburb,
  state,
  postcode,
  country,
  phone,
  email,
  isPrimary,
  onNameChange,
  onCodeChange,
  onDescriptionChange,
  onAddressLinesChange,
  onSuburbChange,
  onStateChange,
  onPostcodeChange,
  onCountryChange,
  onPhoneChange,
  onEmailChange,
  onPrimaryChange,
}: {
  name: string;
  code: string;
  description: string;
  addressLines: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  phone: string;
  email: string;
  isPrimary: boolean;
  onNameChange: (value: string) => void;
  onCodeChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onAddressLinesChange: (value: string) => void;
  onSuburbChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onPostcodeChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPrimaryChange: (value: boolean) => void;
}) {
  return (
    <>
      <label>
        Location name
        <input required value={name} onChange={(event) => onNameChange(event.target.value)} />
      </label>
      <label>
        Location code
        <input value={code} onChange={(event) => onCodeChange(event.target.value)} />
      </label>
      <label className="organisation-profile-description">
        Description
        <textarea
          value={description}
          onChange={(event) => onDescriptionChange(event.target.value)}
        />
      </label>
      <label className="organisation-profile-description">
        Physical address
        <textarea
          required
          placeholder="One line per address line"
          value={addressLines}
          onChange={(event) => onAddressLinesChange(event.target.value)}
        />
      </label>
      <label>
        Suburb
        <input required value={suburb} onChange={(event) => onSuburbChange(event.target.value)} />
      </label>
      <label>
        State or territory
        <input required value={state} onChange={(event) => onStateChange(event.target.value)} />
      </label>
      <label>
        Postcode
        <input
          required
          value={postcode}
          onChange={(event) => onPostcodeChange(event.target.value)}
        />
      </label>
      <label>
        Country
        <input required value={country} onChange={(event) => onCountryChange(event.target.value)} />
      </label>
      <label>
        Phone
        <input value={phone} onChange={(event) => onPhoneChange(event.target.value)} />
      </label>
      <label>
        Email
        <input type="email" value={email} onChange={(event) => onEmailChange(event.target.value)} />
      </label>
      <label>
        Primary location
        <input
          checked={isPrimary}
          type="checkbox"
          onChange={(event) => onPrimaryChange(event.target.checked)}
        />
      </label>
    </>
  );
}

function ProfilePersonnelList({
  apiUrl,
  accessToken,
  tenantId,
  filterName,
  filterValue,
  platformSupportReason,
}: {
  apiUrl: string;
  accessToken: string;
  tenantId: string;
  filterName: 'locationId' | 'teamId';
  filterValue: string;
  platformSupportReason?: string | undefined;
}) {
  const [people, setPeople] = useState<Membership[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const parameters = new URLSearchParams({ [filterName]: filterValue, status: 'active' });
    void apiRequest<PaginatedResponse<Membership>>({
      apiUrl,
      accessToken,
      platformSupportReason,
      path: `/v1/tenants/${tenantId}/memberships?${parameters}`,
    })
      .then((body) => active && setPeople(body.items))
      .catch(() => active && setError('Personnel could not be loaded for this profile.'));
    return () => {
      active = false;
    };
  }, [accessToken, apiUrl, filterName, filterValue, platformSupportReason, tenantId]);

  return (
    <section
      className="location-personnel-list"
      aria-labelledby={`${filterName}-${filterValue}-people`}
    >
      <h3 id={`${filterName}-${filterValue}-people`}>Personnel</h3>
      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}
      {people.length === 0 ? (
        <p className="empty-state">No active personnel are assigned.</p>
      ) : null}
      <ul>
        {people.map((person) => (
          <li key={person.userId}>
            <strong>{person.displayName ?? person.email}</strong>
            <span>{person.jobTitle ?? person.email}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LocationSummary({ location }: { location: LocationListItem }) {
  return (
    <dl className="organisation-page-summary">
      <div>
        <dt>Address</dt>
        <dd>{addressLabel(location)}</dd>
      </div>
      <div>
        <dt>Contact</dt>
        <dd>
          {[location.phone, location.contactEmail].filter(Boolean).join(' · ') || 'Not provided'}
        </dd>
      </div>
      <div>
        <dt>Personnel</dt>
        <dd>{location.personnelCount} active people</dd>
      </div>
    </dl>
  );
}

export function TeamDirectoryPanel({
  apiUrl,
  accessToken,
  tenantId,
  editable,
  managerChoices,
  locations,
  platformSupportReason,
  onChanged,
}: {
  apiUrl: string;
  accessToken: string;
  tenantId: string;
  editable: boolean;
  managerChoices: Membership[];
  locations: OrganisationLocation[];
  platformSupportReason?: string | undefined;
  onChanged: () => Promise<void>;
}) {
  const [items, setItems] = useState<TeamListItem[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput);
  const [status, setStatus] = useState('active');
  const [managerUserId, setManagerUserId] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState<TeamListItem>();
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [managerUserIds, setManagerUserIds] = useState<string[]>([]);

  const activeManagers = useMemo(
    () =>
      managerChoices.filter(
        (membership) =>
          membership.status === 'active' &&
          membership.roles.some((role) =>
            ['tenant_admin', 'manager', 'team_leader'].includes(role),
          ),
      ),
    [managerChoices],
  );
  useEffect(() => setPage(1), [managerUserId, search, status]);
  useEffect(() => {
    let active = true;
    const parameters = new URLSearchParams({ page: String(page), status });
    if (search) parameters.set('search', search);
    if (managerUserId) parameters.set('managerUserId', managerUserId);
    void apiRequest<PaginatedResponse<TeamListItem>>({
      apiUrl,
      accessToken,
      platformSupportReason,
      path: `/v1/tenants/${tenantId}/teams?${parameters}`,
    })
      .then((body) => {
        if (!active) return;
        setItems(body.items);
        setTotal(body.total);
        setTotalPages(body.totalPages);
      })
      .catch(() => active && setError('Teams could not be loaded.'));
    return () => {
      active = false;
    };
  }, [
    accessToken,
    apiUrl,
    managerUserId,
    page,
    platformSupportReason,
    refresh,
    search,
    status,
    tenantId,
  ]);

  const payload = () => ({
    name: name.trim(),
    ...(description.trim() ? { description: description.trim() } : {}),
    locationIds,
    ...(managerUserIds.length ? { managerUserIds } : {}),
  });
  const resetForm = () => {
    setName('');
    setDescription('');
    setLocationIds([]);
    setManagerUserIds([]);
  };
  const createTeam = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Enter a team name.');
      return;
    }
    try {
      await apiRequest({
        apiUrl,
        accessToken,
        platformSupportReason,
        path: `/v1/tenants/${tenantId}/teams`,
        method: 'POST',
        body: payload(),
      });
      resetForm();
      setRefresh((value) => value + 1);
      await onChanged();
    } catch {
      setError('The team could not be created.');
    }
  };
  const openTeam = (team: TeamListItem) => {
    setSelected(team);
    setName(team.name);
    setDescription(team.description ?? '');
    setLocationIds(team.locationIds);
    setManagerUserIds(team.managerUserIds ?? []);
  };
  const saveTeam = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    setError('');
    try {
      const updated = await apiRequest<TeamListItem>({
        apiUrl,
        accessToken,
        platformSupportReason,
        path: `/v1/tenants/${tenantId}/teams/${selected.id}`,
        method: 'PATCH',
        body: payload(),
      });
      setSelected(updated);
      setRefresh((value) => value + 1);
      await onChanged();
    } catch {
      setError('The team could not be saved.');
    }
  };
  const archiveTeam = async () => {
    if (!selected || !window.confirm(`Archive ${selected.name}?`)) return;
    try {
      await apiRequest({
        apiUrl,
        accessToken,
        platformSupportReason,
        path: `/v1/tenants/${tenantId}/teams/${selected.id}`,
        method: 'PATCH',
        body: { status: 'archived' },
      });
      setSelected(undefined);
      setRefresh((value) => value + 1);
      await onChanged();
    } catch {
      setError('The team could not be archived.');
    }
  };
  const managerNames = (team: OrganisationTeam) =>
    (team.managerUserIds ?? [])
      .map((id) => activeManagers.find((member) => member.userId === id)?.email ?? id)
      .join(', ') || 'No managers assigned';

  if (selected)
    return (
      <section className="organisation-structure-panel" aria-labelledby="team-profile-title">
        <button type="button" onClick={() => setSelected(undefined)}>
          ← Back to teams
        </button>
        <div className="workspace-heading">
          <div>
            <p className="eyebrow">Team profile</p>
            <h2 id="team-profile-title">{selected.name}</h2>
          </div>
          <span>{selected.status ?? 'active'}</span>
        </div>
        {editable ? (
          <form className="team-form" onSubmit={(event) => void saveTeam(event)}>
            <TeamFields
              name={name}
              description={description}
              locationIds={locationIds}
              managerUserIds={managerUserIds}
              locations={locations}
              managers={activeManagers}
              onNameChange={setName}
              onDescriptionChange={setDescription}
              onLocationsChange={setLocationIds}
              onManagersChange={setManagerUserIds}
            />
            <button type="submit">Save team</button>
            <button type="button" onClick={() => void archiveTeam()}>
              Archive team
            </button>
          </form>
        ) : (
          <dl className="organisation-page-summary">
            <div>
              <dt>Description</dt>
              <dd>{selected.description || 'No description'}</dd>
            </div>
            <div>
              <dt>Managers</dt>
              <dd>{managerNames(selected)}</dd>
            </div>
            <div>
              <dt>Members</dt>
              <dd>{selected.memberCount} active people</dd>
            </div>
          </dl>
        )}
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
        <ProfilePersonnelList
          accessToken={accessToken}
          apiUrl={apiUrl}
          filterName="teamId"
          filterValue={selected.id}
          platformSupportReason={platformSupportReason}
          tenantId={tenantId}
        />
      </section>
    );

  return (
    <section className="organisation-structure-panel" aria-labelledby="teams-title">
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Organisation teams</p>
          <h2 id="teams-title">Teams</h2>
        </div>
        <div className="personnel-heading-actions">
          <label>
            <span className="visually-hidden">Search teams</span>
            <input
              placeholder="Search teams"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>
          <span>{total} teams</span>
        </div>
      </div>
      <div className="personnel-directory-controls" role="search">
        <label>
          Status
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="all">All statuses</option>
          </select>
        </label>
        <label>
          Manager
          <select value={managerUserId} onChange={(event) => setManagerUserId(event.target.value)}>
            <option value="">All managers</option>
            {activeManagers.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.email}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => {
            setSearchInput('');
            setStatus('active');
            setManagerUserId('');
          }}
        >
          Reset filters
        </button>
      </div>
      {editable ? (
        <form className="team-form" onSubmit={(event) => void createTeam(event)}>
          <TeamFields
            name={name}
            description={description}
            locationIds={locationIds}
            managerUserIds={managerUserIds}
            locations={locations}
            managers={activeManagers}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onLocationsChange={setLocationIds}
            onManagersChange={setManagerUserIds}
          />
          <button type="submit">Add team</button>
        </form>
      ) : null}
      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="structure-list">
        {items.length === 0 ? <p className="empty-state">No teams match these filters.</p> : null}
        {items.map((team) => (
          <article key={team.id}>
            <div>
              <strong>{team.name}</strong>
              <small>{managerNames(team)}</small>
              <small>
                {team.memberCount} active members · {team.status ?? 'active'}
              </small>
            </div>
            <button type="button" onClick={() => openTeam(team)}>
              Open
            </button>
          </article>
        ))}
      </div>
      <ListPagination
        label="Teams"
        page={page}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </section>
  );
}

function TeamFields({
  name,
  description,
  locationIds,
  managerUserIds,
  locations,
  managers,
  onNameChange,
  onDescriptionChange,
  onLocationsChange,
  onManagersChange,
}: {
  name: string;
  description: string;
  locationIds: string[];
  managerUserIds: string[];
  locations: OrganisationLocation[];
  managers: Membership[];
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onLocationsChange: (value: string[]) => void;
  onManagersChange: (value: string[]) => void;
}) {
  return (
    <>
      <label>
        Team name
        <input required value={name} onChange={(event) => onNameChange(event.target.value)} />
      </label>
      <label className="organisation-profile-description">
        Description
        <textarea
          value={description}
          onChange={(event) => onDescriptionChange(event.target.value)}
        />
      </label>
      <label>
        Associated locations
        <select
          multiple
          value={locationIds}
          onChange={(event) => onLocationsChange(selectedIds(event))}
        >
          {locations
            .filter((location) => location.status !== 'archived')
            .map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
        </select>
      </label>
      <label>
        Team managers
        <select
          multiple
          value={managerUserIds}
          onChange={(event) => onManagersChange(selectedIds(event))}
        >
          {managers.map((manager) => (
            <option key={manager.userId} value={manager.userId}>
              {manager.email}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}
